/**
 * Recover a Web Audio context from browser power-management/autoplay states.
 *
 * Mobile Safari may report `interrupted` rather than `suspended` after a long
 * non-audio task (including an offline AI audition). A one-frame silent source
 * created inside the user's gesture also unlocks iOS versions where resume()
 * alone resolves without making scheduled sources audible.
 */
const pendingActivations = new WeakMap();
export const AUDIO_ACTIVATION_TIMEOUT_MS = 2000;

export async function activateAudioContext(context) {
  if (!context || context.state === 'closed') return false;
  if (context.state === 'running') return true;
  if (pendingActivations.has(context)) return pendingActivations.get(context);

  let timer, cleanup = () => {};
  const resume = (async () => {
    try {
      const source = context.createBufferSource?.();
      const gain = context.createGain?.();
      if (source && gain && context.destination && context.createBuffer) {
        source.buffer = context.createBuffer(1, 1, context.sampleRate || 44100);
        gain.gain.value = 0;
        source.connect(gain);
        gain.connect(context.destination);
        cleanup = () => {
          try { source.disconnect(); } catch (_) { /* already disconnected */ }
          try { gain.disconnect(); } catch (_) { /* already disconnected */ }
        };
        source.onended = cleanup;
        source.start(0);
      }
    } catch (_) {
      // Priming is a Safari compatibility assist. resume() remains authoritative.
    }

    try {
      await context.resume();
    } catch (_) {
      return false;
    }
    return context.state === 'running';
  })();
  // Browsers can leave resume() pending indefinitely when output is blocked.
  // A pending entry otherwise makes every later Play reuse the same stuck
  // promise, also leaving the transport's start lock held forever.
  const activation = Promise.race([
    resume,
    new Promise(resolve => { timer = setTimeout(() => resolve(false), AUDIO_ACTIVATION_TIMEOUT_MS); })
  ]);
  pendingActivations.set(context, activation);
  try {
    const running = await activation;
    if (!running) cleanup();
    return running;
  } finally {
    clearTimeout(timer);
    pendingActivations.delete(context);
  }
}

export default activateAudioContext;
